import React, { useState } from "react";
import { useDispatch } from "react-redux";
import classnames from "classnames";
import PropTypes from "prop-types";
import formatUSD from "utils/formatUSD";
import {  Image } from "@sparefoot/react-commons";
import SelfStorageSchema from "@sparefoot/react-commons/SelfStorageSchema";

import { ClickToCall } from "components/brand/ClickToCall";
import { Button } from "components/core/Button";
import { Link } from "components/core/Link";
import { Text } from "components/core/Typography";
import {  MapPinFilled } from "components/core/Icons";
import { StarReviewsCount } from "components/molecules/review/StarReviewsCount";
import { getLocalNumber } from "store/modules/phone";
import FeeTooltip from "components/facility/FeeTooltip/FeeTooltip";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";
import OMIBanner from "components/organisms/search/OMIBanner/OMIBanner";
import { mapAmenitiesToFeature } from "config/amenityMap";

import "./FacilityCardV2.scss";

const FacilityTitle = ({ name, address, url }) => (
    <div className="facility-title">
        <Link
            href={url}
            last
            display="block"
            weight="bold"
            className="facility-name"
            color="black"
        >
            {name}
        </Link>

        <Text
            last
            display="block"
            className="facility-address"
            mediumSize="small"
        >
            {address}
        </Text>
    </div>
);
FacilityTitle.propTypes = {
    name: PropTypes.string,
    address: PropTypes.string,
    url: PropTypes.string
};

const FacilityDistance = ({ distance }) => (
    <div className="facility-distance">
        <MapPinFilled fill="blue" />
        <Text
            last
            size="small"
            className="distance-text"
        >
            <span className="distance-number">{distance}</span>
            <span className="distance-label"> miles away</span>
        </Text>
    </div>
);
FacilityDistance.propTypes = {
    distance: PropTypes.number
};

const FacilityImage = ({
    src,
    showDefaultImage,
    alt,
    hasOnlineMoveins,
    lazyLoad
}) => (
    <div
        className={classnames("facility-image", {
             "show-default": showDefaultImage
        })}
    >
        <Image
            lazy={lazyLoad}
            src={src}
            alt={alt}
        />
        {showDefaultImage && (
            <div className="default-image">
                <Text
                    last
                    weight="bold"
                    color="white"
                >
                    No facility image
                </Text>
            </div>
        )}
        {hasOnlineMoveins && (
            <div className="omi-banner-overlay">
                <OMIBanner />
            </div>
        )}
    </div>
);

FacilityImage.propTypes = {
    showDefaultImage: PropTypes.bool,
    src: PropTypes.string,
    alt: PropTypes.string,
    hasOnlineMoveins: PropTypes.bool,
    lazyLoad: PropTypes.bool
};

FacilityImage.defaultProps = {
    lazyLoad: true
};

const FacilityPrice = ({
    startingPrice,
    lowestPrice,
    totalFees,
    fees,
    showFeeWarning
}) => {
    const showStartingPrice = startingPrice && lowestPrice < startingPrice;
    return (
        <div className="facility-price">
            <Text
                last
                size="small"
                className="facility-price-text"
            >
                Starting at
            </Text>
            <div className="prices">
                <Text
                    last
                    color="green"
                    className="lowest-price"
                >
                    {formatUSD(lowestPrice, { showDecimal: true })}
                </Text>
                {showStartingPrice && (
                    <Text
                        last
                        size="small"
                        strikethrough
                        className="starting-price"
                    >
                        {formatUSD(startingPrice, { showDecimal: true })}
                    </Text>
                )}
            </div>
            {showFeeWarning && totalFees > 0 && (
                <FeeTooltip
                    fees={fees}
                    totalFees={totalFees}
                />
            )}
        </div>
    );
};
FacilityPrice.propTypes = {
    startingPrice: PropTypes.number,
    lowestPrice: PropTypes.number,
    fees: PropTypes.arrayOf(PropTypes.object),
    showFeeWarning: PropTypes.bool,
    totalFees: PropTypes.number
};

const ReserveNowButton = ({ url }) => {
    const handleViewUnits = () => {
        trackCTAClick(
            'view-all-units',
            'View all units/prices Button',
            'View All Units',
            url,
        );
        window.location.assign(url);
    }
    return (
        <Button
            href={url}
            onClick={handleViewUnits}
            theme="primary"
            className="reserve-button"
        >
            View All Units
        </Button>
    )
};
ReserveNowButton.propTypes = {
    url: PropTypes.string
};

// --------------------------------------------------
const FacilityCardV2 = ({
    name,
    layout,
    image,
    showDefaultImage,
    address,
    distance,
    url,
    lowestPrice,
    startingPrice,
    reviews,
    schema,
    totalFees,
    lockFee,
    adminFee,
    showFeeWarning,
    hasOnlineMoveins,
    lazyLoad
}) => {
    const [phone, setPhone] = useState({});
    const dispatch = useDispatch();
    const handleFetchNumber = async ({ city, state }) => {
        const data = { path: `${city}/${state}` };
        const {
            payload: { result }
        } = await dispatch(getLocalNumber(data));
        setPhone(result);
        return result;
    };

    const fees = [
        {
            name: "One-Time Admin Fee",
            price: adminFee
        },
        {
            name: "One-Time Lock Fee",
            price: lockFee
        }
    ];

    const amenityFeature = mapAmenitiesToFeature({
        amenities: schema?.amenities || {}
    });

    const staticMapUrl = schema?.staticMapUrl;
    const paymentAccepted = schema?.paymentAccepted;
    const openingHours =
        schema?.openingHours || schema?.openingHoursSpecification;
    const sameAs = Array.isArray(schema?.sameAs) ? schema.sameAs : undefined;

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            className="facility-card-new-v2"
        >
            <SelfStorageSchema
                {...schema}
                amenityFeature={amenityFeature}
                staticMapUrl={staticMapUrl}
                paymentAccepted={paymentAccepted}
                openingHours={openingHours}
                sameAs={sameAs}
            />
            <div className="card-main">
                <div
                    className={classnames("card-top", `card-layout-${layout}`)}
                >
                    <FacilityImage
                        src={image}
                        showDefaultImage={showDefaultImage}
                        alt={name}
                        hasOnlineMoveins={hasOnlineMoveins}
                        lazyLoad={lazyLoad}
                    />
                 </div>
                <div className="card-details-container">
                    <div className="card-details-top">
                        <div className="review-section">
                            {(distance > 0 &&
                                <FacilityDistance distance={distance} />)}
                            {reviews.number > 0 && (
                                <StarReviewsCount
                                    className="facility-reviews"
                                    url={`${url}#reviews`}
                                    number={reviews.number}
                                    average={reviews.average}
                                    withLabel
                                    facilityCard
                                />)}
                        </div>
                        <FacilityTitle
                            url={url}
                            name={name}
                            address={address}
                        />
                        <ClickToCall
                            underline
                            handleFetchNumber={() =>
                                handleFetchNumber(schema?.location)
                            }
                            phone={phone}
                            textFontWeight="bold"
                        />
                    </div>
                    <div className="card-details-bottom">
                        <FacilityPrice
                            startingPrice={startingPrice}
                            lowestPrice={lowestPrice}
                            fees={fees}
                            totalFees={totalFees}
                            showFeeWarning={showFeeWarning}
                        />
                        <ReserveNowButton url={url} />
                    </div>
                </div>
            </div>
        </div>
    );
};

FacilityCardV2.propTypes = {
    name: PropTypes.string,
    layout: PropTypes.oneOf(["wide", "narrow"]),
    image: PropTypes.string,
    address: PropTypes.string,
    distance: PropTypes.number,
    url: PropTypes.string,
    startingPrice: PropTypes.number,
    lowestPrice: PropTypes.number,
    showDefaultImage: PropTypes.bool,
    reviews: PropTypes.shape({
        average: PropTypes.number,
        number: PropTypes.number
    }),
    schema: PropTypes.object,
    totalFees: PropTypes.number,
    lockFee: PropTypes.number,
    adminFee: PropTypes.number,
    showFeeWarning: PropTypes.bool,
    hasOnlineMoveins: PropTypes.bool,
    lazyLoad: PropTypes.bool
};

FacilityCardV2.defaultProps = {
    layout: "wide",
    reviews: {
        average: 0,
        number: 0
    },
    lazyLoad: true
};
export default FacilityCardV2;
